import React from "react";
import "style/MsTrader.scss";

import { Link } from "react-router-dom";

import landing from "config/i18n/landing";
import useLang from "hooks/useLang";
import usePageTitle from "hooks/usePageTitle";

import Reveal from "components/Reveal";
import MsTraderDiagram from "components/MsTraderDiagram";

const MsTraderArchitecture = () => {
  const lang = useLang();
  const t = landing[lang].architecture;
  const common = landing[lang].common;
  usePageTitle(t.pageTitle);

  return (
    <div className="lp lp--architecture">
      <header className="lp-hero lp-hero--compact">
        <div className="lp-hero__grid" aria-hidden="true" />
        <div className="lp-hero__overlay" aria-hidden="true" />

        <div className="shell lp-hero__inner lp-hero__inner--single">
          <div className="lp-hero__copy">
            <Link className="lp-hero__back" to="/ms-trader">
              ← MS Trader
            </Link>
            <span className="lp-hero__eyebrow">{t.eyebrow}</span>
            <h1 className="lp-hero__title">
              {t.title[0]}
              <br />
              <span className="text-gradient">{t.title[1]}</span>
            </h1>
            <p className="lp-hero__lead">{t.lead}</p>
          </div>
        </div>
      </header>

      <main>
        {/* Services */}
        <section className="lp-section lp-section--light">
          <div className="shell">
            <Reveal className="section-head">
              <h2>
                {t.services.title[0]}{" "}
                <span className="accent">{t.services.title[1]}</span>
              </h2>
            </Reveal>
            <Reveal className="lp-table">
              <table>
                <thead>
                  <tr>
                    {t.services.head.map((head) => (
                      <th key={head} scope="col">
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {t.services.rows.map((row) => (
                    <tr key={row[0]}>
                      <td>
                        <code>{row[0]}</code>
                      </td>
                      <td>{row[1]}</td>
                      <td>{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Reveal>
          </div>
        </section>

        {/* Data flow */}
        <section className="lp-section lp-section--dark">
          <div className="shell">
            <Reveal className="section-head">
              <h2>
                {t.flow.title[0]}{" "}
                <span className="accent">{t.flow.title[1]}</span>
              </h2>
            </Reveal>
            <Reveal>
              <div className="lp-diagram">
                <MsTraderDiagram ariaLabel={t.flow.caption} />
              </div>
              <p className="lp-caption">{t.flow.caption}</p>
            </Reveal>

            <Reveal className="section-head section-head--spaced">
              <h2>
                {t.sessions.title[0]}{" "}
                <span className="accent">{t.sessions.title[1]}</span>
              </h2>
            </Reveal>
            <Reveal className="lp-table">
              <table>
                <thead>
                  <tr>
                    {t.sessions.head.map((head) => (
                      <th key={head} scope="col">
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {t.sessions.rows.map((row) => (
                    <tr key={row[0]}>
                      <td>{row[0]}</td>
                      <td>
                        <code>{row[1]}</code>
                      </td>
                      <td>{row[2]}</td>
                      <td>{row[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Reveal>
            <p className="lp-note">{t.sessions.note}</p>

            <div className="lp-grid lp-grid--2 lp-grid--spaced">
              {t.databases.items.map((item, index) => (
                <Reveal key={item.title} className="lp-cell" delay={index * 80}>
                  <article className="lp-card">
                    <span className="lp-card__index">DB</span>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FIX capabilities */}
        <section className="lp-section lp-section--light">
          <div className="shell">
            <Reveal className="section-head">
              <h2>
                {t.fix.title[0]}{" "}
                <span className="accent">{t.fix.title[1]}</span>
              </h2>
              <p className="section-head__lead">{t.fix.lead}</p>
            </Reveal>

            <div className="lp-fixgroups">
              {t.fix.groups.map((group, index) => (
                <Reveal key={group.title} className="lp-chipset" delay={index * 60}>
                  <h3>{group.title}</h3>
                  <div className="lp-chips">
                    {group.chips.map((chip) => (
                      <span className="chip" key={chip}>
                        {chip}
                      </span>
                    ))}
                  </div>
                </Reveal>
              ))}
              <Reveal className="lp-chipset lp-chipset--text" delay={t.fix.groups.length * 60}>
                <h3>{t.fix.dictionary.title}</h3>
                <p>{t.fix.dictionary.text}</p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Resilience */}
        <section className="lp-section lp-section--muted">
          <div className="shell">
            <Reveal className="section-head">
              <h2>
                {t.resilience.title[0]}{" "}
                <span className="accent">{t.resilience.title[1]}</span>
              </h2>
            </Reveal>
            <div className="lp-grid">
              {t.resilience.items.map((item, index) => (
                <Reveal key={item.title} className="lp-cell" delay={index * 60}>
                  <article className="lp-card">
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Security & operations */}
        <section className="lp-section lp-section--dark">
          <div className="shell">
            <Reveal className="section-head">
              <h2>
                {t.security.title[0]}{" "}
                <span className="accent">{t.security.title[1]}</span>
              </h2>
            </Reveal>
            <Reveal>
              <ul className="lp-list lp-list--2col">
                {t.security.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        <section className="lp-section lp-section--light">
          <div className="shell">
            <Reveal className="lp-cta">
              <div>
                <h2>{t.cta.title}</h2>
                <p>{t.cta.text}</p>
              </div>
              <div className="lp-cta__actions">
                <Link className="btn btn--primary" to="/contact">
                  {t.cta.button}
                </Link>
                <Link className="btn btn--ghost" to="/ms-trader">
                  {common.back}
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </div>
  );
};

export default MsTraderArchitecture;
